// useWidgetVisibility.ts
// Resolves SYSOption-based visibility for all widgets and returns a patched
// copy of the registry where isHidden already incorporates the option.
// Callers only ever need to call widget.isHidden?.(meData) — nothing else.

import { useContext } from 'react';
import { useQuery } from '@apollo/client';
import { ActiveDatabaseContext } from '../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import optionQuery from '../../Components/QueryComponents/AvenySYSOptionQuery/AvenySYSOptionQuery.gql';
import {
  AvenySYSOptionQuery,
  AvenySYSOptionQueryVariables,
} from '../../Components/QueryComponents/AvenySYSOptionQuery/_QueryTypes/AvenySYSOptionQuery';
import { IWidget } from './Widgets';

/**
 * Declares which SYSOption controls a widget's availability.
 * if the option is empty / not configured for the widget do not patch visibility
 *
 * The option value is a comma-separated string from the database,
 * e.g. "All,Kalender,Statistik". The widget is visible when:
 *   - the list contains "All" (wildcard), OR
 *   - the list contains one of `allowedValues` (case-insensitive).
 */
export interface IWidgetVisibilityOption {
  subSystemCode: string;
  optionKey: string;
  allowedValues: string[];
}

function isAllowedByOption(
  optionData: string | null | undefined,
  allowedValues: string[]
): boolean {
  if (!optionData) return true;
  const parts = optionData.split(',').map(s => s.trim().toLowerCase());
  if (parts.includes('all')) return true;
  return allowedValues.some(v => parts.includes(v.toLowerCase()));
}

function optionCacheKey(opt: IWidgetVisibilityOption): string {
  return `${opt.subSystemCode}__${opt.optionKey}`;
}

/**
 * Fetches all SYSOption keys declared by the widget registry and returns a
 * patched copy of the each widget's `isHidden`
 *
 * Callers need no knowledge of visibilityOption — just call:
 *   widget.isHidden?.(meData)
 *
 * While options are loading, `isHidden` returns true (hide widget) so a widget
 * is only shown once its visibility is confirmed.
 */
export function useWidgetVisibility(
  widgets: Record<string, IWidget>
): Record<string, IWidget> {
  const { activeDatabase } = useContext(ActiveDatabaseContext);

  // Collect unique option configs needed across the entire widget set.
  const uniqueOptions = Object.values(widgets).reduce<
    Map<string, IWidgetVisibilityOption>
  >((acc, widget) => {
    if (widget.visibilityOption) {
      const key = optionCacheKey(widget.visibilityOption);
      if (!acc.has(key)) acc.set(key, widget.visibilityOption);
    }
    return acc;
  }, new Map());

  // Stable sorted list — derived from the module-level `widgets` constant so
  // the number of useQuery calls never changes between renders.
  const optionConfigs = Array.from(uniqueOptions.values()).sort((a, b) =>
    optionCacheKey(a).localeCompare(optionCacheKey(b))
  );

  /* eslint-disable react-hooks/rules-of-hooks */
  const queryResults = optionConfigs.map(opt =>
    useQuery<AvenySYSOptionQuery, AvenySYSOptionQueryVariables>(optionQuery, {
      fetchPolicy: 'cache-and-network',
      variables: {
        dbName: activeDatabase ?? '',
        subSystemCode: opt.subSystemCode,
        optionKey: opt.optionKey,
      },
      skip: !activeDatabase,
    })
  );
  /* eslint-enable react-hooks/rules-of-hooks */

  // cacheKey → resolved option string; undefined means still loading.
  const resolvedOptions = new Map<string, string | null | undefined>(
    optionConfigs.map((opt, i) => [
      optionCacheKey(opt),
      queryResults[i].loading
        ? undefined
        : queryResults[i].data?.db?.SYSOptions_All?.[0]?.OptionData,
    ])
  );

  // Return a patched widget: each widget gets an isHidden that combines the
  // option result with whatever logic was already on the widget.
  return Object.fromEntries(
    Object.values(widgets).map(widget => {
      const { visibilityOption, isHidden: domainIsHidden } = widget;

      if (!visibilityOption) return [widget.id, widget];

      const key = optionCacheKey(visibilityOption);
      const optionData = resolvedOptions.get(key);
      // Still loading → hide until confirmed visible.
      const hiddenByOption =
        optionData === undefined ||
        !isAllowedByOption(optionData, visibilityOption.allowedValues);

      const patchedWidget: IWidget = {
        ...widget,
        isHidden: meData =>
          hiddenByOption || (domainIsHidden?.(meData) ?? false),
      };

      return [widget.id, patchedWidget];
    })
  );
}
